// Row unnumbered (instruction-named, not a CAT28 matrix row — disclosed as such in the closeout).
// Real system status: calls the actual backend health endpoints (main.py:169-181), never a fake
// status page. Needs vite.config.ts's dev-proxy entries for /health, /ready, /live (added this
// batch) to actually reach the backend in local dev.
import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@perlab/ui";

type ProbeState = "checking" | "ok" | "down";

interface ProbeRowData {
  key: string;
  path: string;
}

const PROBES: ProbeRowData[] = [
  { key: "health", path: "/health" },
  { key: "ready", path: "/ready" },
  { key: "live", path: "/live" },
];

function useProbe(path: string): ProbeState {
  const [state, setState] = useState<ProbeState>("checking");
  useEffect(() => {
    let cancelled = false;
    setState("checking");
    fetch(path, { method: "GET" })
      .then((res) => {
        if (!cancelled) setState(res.ok ? "ok" : "down");
      })
      .catch(() => {
        if (!cancelled) setState("down");
      });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return state;
}

function ProbeRow({ probeKey, path }: { probeKey: string; path: string }): ReactElement {
  const { t } = useTranslation();
  const state = useProbe(path);
  return (
    <div className="flex items-center justify-between border-t border-[var(--color-border-subtle)] py-2 first:border-t-0">
      <span className="text-sm text-[var(--color-text-primary)]">{t(`status.${probeKey}`)}</span>
      <span
        className={
          "text-xs font-medium " +
          (state === "ok"
            ? "text-[var(--color-system-status-success,#0AC275)]"
            : state === "down"
              ? "text-[var(--color-danger-500)]"
              : "text-[var(--color-text-secondary)]")
        }
      >
        {t(`status.${state}`)}
      </span>
    </div>
  );
}

export interface SystemStatusPanelProps {
  open: boolean;
  onClose: () => void;
}

export function SystemStatusPanel({ open, onClose }: SystemStatusPanelProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} title={t("status.title")}>
      <div className="flex flex-col">
        {PROBES.map((p) => (
          <ProbeRow key={p.key} probeKey={p.key} path={p.path} />
        ))}
      </div>
    </Modal>
  );
}
